import React, {ReactNode} from 'react';
import {v4 as uuidv4} from 'uuid';

import PrimaryExperimentalRoleSymbol from 'samples/experimental-roles/PrimaryExperimentalRoleSymbol';
import MulticolorStripedCircle from 'samples/plate-maps/MulticolorStripedCircle';
import {DisplayPlateMapWell} from 'samples/plate-maps/PlateMapConstants';
import ValidationSymbolWell from 'samples/plate-maps/ValidationSymbolWell';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE, BLUE_LIGHT, BLUE_POWDER_LIGHT, GRAY, NEAR_BLACK, WHITE} from 'ui-library/Colors';

/**
 * Renders a circle with a left-to-right gradient (white to color) and three dots in the center.
 * Used to indicate wells with multiple measurement values (MULTIPLE_VALUES_SENTINEL).
 */
function GradientCircleWell({
  cx,
  cy,
  radius,
  color,
}: {
  cx: number;
  cy: number;
  radius: number;
  color: string;
}): JSX.Element {
  const uuid = uuidv4();
  const gradientId = `gradient-${uuid}`;
  const dotRadius = 1.5;
  const dotSpacing = 4;

  return (
    <g>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={WHITE} />
          <stop offset="100%" stopColor={color} />
        </linearGradient>
      </defs>
      <circle cx={cx} cy={cy} r={radius} fill={`url(#${gradientId})`} stroke={NEAR_BLACK} strokeWidth={1} />
      {/* Three dots to indicate multiple values */}
      <circle cx={cx - dotSpacing} cy={cy} r={dotRadius} fill={NEAR_BLACK} />
      <circle cx={cx} cy={cy} r={dotRadius} fill={NEAR_BLACK} />
      <circle cx={cx + dotSpacing} cy={cy} r={dotRadius} fill={NEAR_BLACK} />
    </g>
  );
}

export interface WellRendererProps {
  cx: number;
  cy: number;
  radius: number;
  well: DisplayPlateMapWell;
}

// Renders one or more colored, horizontal stripes in the well.
export function MulticolorStripeWellRenderer({cx, cy, radius, well}: WellRendererProps): JSX.Element {
  // Check if this well should use a gradient
  if (well.displayInfo!.colorInfo.type === 'gradient') {
    return <GradientCircleWell cx={cx} cy={cy} radius={radius} color={well.displayInfo!.colorInfo.color} />;
  }
  return (
    <MulticolorStripedCircle
      cx={cx}
      cy={cy}
      radius={radius}
      colors={well.displayInfo!.colorInfo.colors}
      labelOverride={well.displayInfo!.label ?? undefined}
      fillOpacity={well.displayInfo!.opacity}
    />
  );
}

export function MulticolorStripedWellRendererWithSquareListIcon({
  cx,
  cy,
  radius,
  well,
}: WellRendererProps): JSX.Element {
  // Check if this well should use a gradient
  if (well.displayInfo!.colorInfo.type === 'gradient') {
    return <GradientCircleWell cx={cx} cy={cy} radius={radius} color={well.displayInfo!.colorInfo.color} />;
  }

  return (
    <MulticolorStripedCircle
      cx={cx}
      cy={cy}
      radius={radius}
      colors={well.displayInfo!.colorInfo.colors}
      labelOverride={well.displayInfo!.label ?? undefined}
      fillOpacity={well.displayInfo!.opacity}
      iconOverride="square-list"
    />
  );
}

export function MulticolorStripedOrErrorWellRenderer({cx, cy, radius, well}: WellRendererProps): JSX.Element {
  if (well.displayInfo?.error != null) {
    return <ValidationSymbolWell cx={cx} cy={cy} radius={radius} errorLevel={well.displayInfo.error} />;
  }

  // Check if this well should use a gradient
  if (well.displayInfo?.colorInfo.type === 'gradient') {
    return <GradientCircleWell cx={cx} cy={cy} radius={radius} color={well.displayInfo.colorInfo.color} />;
  }

  return <MulticolorStripedWellRendererWithSquareListIcon cx={cx} cy={cy} radius={radius} well={well} />;
}

// Renders only well role and group information.
export function WellRoleWellRenderer({cx, cy, radius, well}: WellRendererProps): JSX.Element {
  return (
    <PrimaryExperimentalRoleSymbol
      cx={cx}
      cy={cy}
      radius={radius}
      primaryRole={well.role!.primaryRole}
      label={well.displayInfo!.label}
    />
  );
}

interface EmptyWellProps {
  cx: number;
  cy: number;
  wellSize: number;
  isHovered: boolean;
  isSelected: boolean;
}

// Renders an empty well with no information displayed.
export function EmptyWell({cx, cy, wellSize, isHovered, isSelected}: EmptyWellProps): JSX.Element {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={wellSize * 0.5}
      fill={isHovered ? BLUE_POWDER_LIGHT : WHITE}
      stroke={isSelected ? BLUE : GRAY}
      strokeWidth={isSelected ? 2 : 1}
      {...getTestProps({component: 'EmptyWell', element: 'root'})}
    />
  );
}

interface WellRendererWrapperProps extends EmptyWellProps {
  well: DisplayPlateMapWell;
  renderWell: (props: WellRendererProps) => ReactNode;
  isPreview: boolean;
}

/**
 * Handles rendering a circular well at a given position on a plate map with hover and selection state.
 *
 * well: A plate map well with additional display attributes. If null, there is no information to display
 * for the well with this displayLayer, so we render an empty well.
 * renderWell: a function that returns a React component to render the given display well.
 */
export default function WellRendererWrapper(props: WellRendererWrapperProps): JSX.Element {
  const {cx, cy, wellSize, isHovered, isSelected, isPreview, well, renderWell} = props;
  if (well.displayInfo == null) {
    return <EmptyWell {...props} />;
  }
  const fullRadius = wellSize * 0.5;
  const showOverlay = isHovered || isSelected || isPreview;
  const strokeWidth = isSelected ? 2 : 0;
  const stroke = (() => {
    if (isSelected) {
      return isPreview ? BLUE_LIGHT : BLUE;
    }
    return 'none';
  })();
  const fillOpacity = (() => {
    if (isHovered) {
      return 0.2;
    }
    if (isPreview) {
      return 0.4;
    }
    return 0;
  })();

  return (
    <g>
      {renderWell({
        cx,
        cy,
        radius: isSelected ? fullRadius * 0.8 : fullRadius,
        well,
      })}
      <circle
        display={showOverlay ? 'inherit' : 'none'}
        cx={cx}
        cy={cy}
        r={fullRadius}
        fill={WHITE}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}
